
import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Package, Ruler, Layers } from "lucide-react";
import Select from "react-select";
import toast from "react-hot-toast";
import {
  createPackagingVariant,
  updatePackagingVariant,
} from "../api/costingSettingsApi";
import { getBomEstimatedCost } from "../api/bomApi";
import { generateId } from "../utils/generateId";

const lightSelectStyles = {
  control: (b, s) => ({
    ...b,
    minHeight: 38,
    height: 38,
    background: "#fff",
    border: `1px solid ${s.isFocused ? "#111827" : "#d1d5db"}`,
    borderRadius: 10,
    boxShadow: s.isFocused ? "0 0 0 2px rgba(17,24,39,0.08)" : "none",
    fontSize: 13,
    cursor: "pointer",
  }),
  valueContainer: (b) => ({ ...b, padding: "0 12px" }),
  singleValue: (b) => ({ ...b, color: "#111827" }),
  placeholder: (b) => ({ ...b, color: "#9ca3af", fontSize: 13 }),
  indicatorsContainer: (b) => ({ ...b, height: 38 }),
  dropdownIndicator: (b) => ({ ...b, padding: "0 8px", color: "#6b7280" }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (b) => ({
    ...b,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    zIndex: 9999,
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
    overflow: "hidden",
  }),
  option: (b, s) => ({
    ...b,
    background: s.isSelected ? "#111827" : s.isFocused ? "#f3f4f6" : "#fff",
    color: s.isSelected ? "#fff" : "#111827",
    fontSize: 13,
    padding: "9px 12px",
    cursor: "pointer",
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
};

const cellSelectStyles = {
  ...lightSelectStyles,
  control: (b, s) => ({
    ...lightSelectStyles.control(b, s),
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    minHeight: 38,
    height: 38,
    "&:hover": { background: "#f9fafb" },
  }),
};

const emptyItem = () => ({
  id: generateId(),
  itemType: "",
  sku: "",
  name: "",
  qty: "",
  unitCost: 0,
  erpagUnitCost: null,
  sheetUnitCost: null,
  unitCostSource: "",
});

const PackagingVariantModal = ({
  isOpen,
  onClose,
  mode = "create",
  initialVariant = null,
  onSuccess,
  ingredientsList = [],
  packagingItemTypeOptions = [],
  canManageCostingSettings,
  ensureSettingsWriteAccess,
}) => {
  const isEditing = mode === "edit";

  const [currentVariantName, setCurrentVariantName] = useState("");
  const [packagingType, setPackagingType] = useState("Bulk");
  const [selectedBulkSize, setSelectedBulkSize] = useState("1kg");
  const [items, setItems] = useState([]);
  const [dimensions, setDimensions] = useState({
    length: { mm: "", in: "" },
    width: { mm: "", in: "" },
    height: { mm: "", in: "" },
  });
  const [palletConfig, setPalletConfig] = useState({ ti: "", hi: "" });
  const [loosePackAllowed, setLoosePackAllowed] = useState("no");
  const [defaultLooseUnits, setDefaultLooseUnits] = useState(0);
  const [defaultCasePack, setDefaultCasePack] = useState(1);
  const [defaultInnerMaster, setDefaultInnerMaster] = useState(0);
  const [savingVariant, setSavingVariant] = useState(false);

  const lastEditedRef = useRef({});

  const MM_PER_INCH = 25.4;

  const roundTo16th = (n) =>
    typeof n === "number" && !isNaN(n) ? Math.round(n * 16) / 16 : "";

  const mmToIn = (mm) => (mm ? roundTo16th(Number(mm) / MM_PER_INCH) : "");

  const inToMm = (ins) => (ins ? Math.round(Number(ins) * MM_PER_INCH) : "");

  const findIngredientBySku = (sku) => {
    const cleanSku = String(sku || "").trim().toUpperCase();
    if (!cleanSku) return null;

    return ingredientsList.find(
      (ing) => String(ing.sku || "").trim().toUpperCase() === cleanSku
    );
  };

  const getAutoName = (found) => {
    return (
      found?.name ||
      found?.customFields?.nameEn ||
      found?.description ||
      found?.descriptionEn ||
      ""
    );
  };

  const getAutoCost = (found) => {
    return Number(found?.unitCost || found?.cost || found?.purchasePrice || 0);
  };

  const resolvePackagingSku = async (sku) => {
    const cleanSku = String(sku || "").trim().toUpperCase();
    if (!cleanSku) {
      return {
        sku: "",
        name: "",
        unitCost: 0,
        unitCostSource: "",
      };
    }

    const found = findIngredientBySku(cleanSku);
    const sheetCost = await getBomEstimatedCost(cleanSku);
    if (found) {
      const resolved = {
        sku: found.sku,
        name: getAutoName(found),
        unitCost: getAutoCost(found),
        erpagUnitCost: getAutoCost(found),
        sheetUnitCost: Number(sheetCost || 0),
        unitCostSource: "erpag",
      };
      console.table([
        {
          sku: resolved.sku,
          erpagCost: resolved.erpagUnitCost,
          sheetCost: resolved.sheetUnitCost,
          selectedCost: resolved.unitCost,
          source: resolved.unitCostSource,
        },
      ]);
      return resolved;
    }

    const resolved = {
      sku: cleanSku,
      name: "",
      unitCost: Number(sheetCost || 0),
      erpagUnitCost: null,
      sheetUnitCost: Number(sheetCost || 0),
      unitCostSource: sheetCost ? "sheet" : "",
    };
    console.table([
      {
        sku: resolved.sku,
        erpagCost: resolved.erpagUnitCost,
        sheetCost: resolved.sheetUnitCost,
        selectedCost: resolved.unitCost,
        source: resolved.unitCostSource || "not found",
      },
    ]);
    return resolved;
  };

  const normalizeItemType = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";

    const found = packagingItemTypeOptions.find(
      (opt) =>
        String(opt.value).toLowerCase() === text.toLowerCase() ||
        String(opt.label).toLowerCase() === text.toLowerCase()
    );

    return found?.value || text;
  };

  const handleDimChange = (dim, unit, value) => {
    lastEditedRef.current[dim] = unit;

    setDimensions((p) => ({
      ...p,
      [dim]: {
        ...p[dim],
        [unit]: value,
      },
    }));
  };

  const handleDimBlur = (dim, unit) => {
    if (lastEditedRef.current[dim] !== unit) return;

    setDimensions((p) => {
      const n = {
        ...p,
        [dim]: {
          ...p[dim],
        },
      };

      const v = n[dim][unit];

      if (unit === "mm") {
        n[dim].mm = v !== "" ? Math.round(Number(v)) : "";
        n[dim].in = mmToIn(v);
      } else {
        n[dim].in = v !== "" ? roundTo16th(Number(v)) : "";
        n[dim].mm = inToMm(v);
      }

      return n;
    });
  };

  const handleItemsPaste = (e, startIndex) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text");
    if (!pastedText.trim()) return;

    const rows = pastedText
      .trim()
      .split(/\r?\n/)
      .map((row) => row.split(/\t|,/).map((col) => col.trim()))
      .filter((cols) => cols.some(Boolean));

    setItems((prev) => {
      const updated = [...prev];

      rows.forEach((cols, rowOffset) => {
        const idx = startIndex + rowOffset;

        while (!updated[idx]) {
          updated.push(emptyItem());
        }

        let itemType = updated[idx].itemType;
        let sku = updated[idx].sku;
        let qty = updated[idx].qty;

        if (cols.length >= 3) {
          itemType = normalizeItemType(cols[0]);
          sku = cols[1] || "";
          qty = cols[2] || "";
        } else if (cols.length === 2) {
          sku = cols[0] || "";
          qty = cols[1] || "";
        } else {
          sku = cols[0] || "";
        }

        updated[idx] = {
          ...updated[idx],
          itemType,
          sku: String(sku || "").trim().toUpperCase(),
          qty,
        };
      });

      return updated;
    });

    rows.forEach((cols, rowOffset) => {
      const idx = startIndex + rowOffset;
      const sku =
        cols.length >= 3
          ? cols[1] || ""
          : cols.length === 2
            ? cols[0] || ""
            : cols[0] || "";

      resolvePackagingSku(sku)
        .then((resolved) => {
          setItems((prev) => {
            if (!prev[idx]) return prev;
            const updated = [...prev];
            const currentSku = String(updated[idx]?.sku || "").trim().toUpperCase();
            if (currentSku !== resolved.sku) return prev;

            updated[idx] = {
              ...updated[idx],
              name: resolved.name,
              unitCost: resolved.unitCost,
              erpagUnitCost: resolved.erpagUnitCost,
              sheetUnitCost: resolved.sheetUnitCost,
              unitCostSource: resolved.unitCostSource,
            };
            return updated;
          });
        })
        .catch((err) => {
          console.warn("Failed to resolve packaging SKU cost:", sku, err);
        });
    });
  };

  const handleSkuChange = (index, value) => {
    const trimmed = value.trim().toUpperCase();

    setItems((prev) => {
      const u = [...prev];
      const found = findIngredientBySku(trimmed);

      u[index] = {
        ...u[index],
        sku: value,
        name: found ? getAutoName(found) : "",
        unitCost: found ? getAutoCost(found) : 0,
        erpagUnitCost: found ? getAutoCost(found) : null,
        sheetUnitCost: null,
        unitCostSource: found ? "erpag" : "",
      };

      return u;
    });

    if (!trimmed || findIngredientBySku(trimmed)) return;

    resolvePackagingSku(trimmed)
      .then((resolved) => {
        setItems((prev) => {
          if (!prev[index]) return prev;
          const currentSku = String(prev[index]?.sku || "").trim().toUpperCase();
          if (currentSku !== trimmed) return prev;

          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            sku: resolved.sku,
            name: resolved.name,
            unitCost: resolved.unitCost,
            erpagUnitCost: resolved.erpagUnitCost,
            sheetUnitCost: resolved.sheetUnitCost,
            unitCostSource: resolved.unitCostSource,
          };
          return updated;
        });
      })
      .catch((err) => {
        console.warn("Failed to resolve packaging SKU cost:", trimmed, err);
      });
  };

  useEffect(() => {
    if (!isOpen) return;

    if (isEditing && initialVariant) {
      setCurrentVariantName(initialVariant.name || "");
      setPackagingType(initialVariant.format || "Bulk");
      setSelectedBulkSize(initialVariant.size || "1kg");

      const normalized = (initialVariant.items || []).map((item) => ({
        id: generateId(),
        itemType: item.itemType || "",
        sku: item.sku || "",
        name: item.name || "",
        qty: item.qty || "",
        unitCost: item.unitCost || 0,
        erpagUnitCost: item.erpagUnitCost ?? null,
        sheetUnitCost: item.sheetUnitCost ?? null,
        unitCostSource: item.unitCostSource || "",
      }));

      setItems(normalized.length ? normalized : [emptyItem()]);

      setDimensions({
        length: {
          mm: initialVariant.lengthMm ?? "",
          in: initialVariant.lengthIn ?? "",
        },
        width: {
          mm: initialVariant.widthMm ?? "",
          in: initialVariant.widthIn ?? "",
        },
        height: {
          mm: initialVariant.heightMm ?? "",
          in: initialVariant.heightIn ?? "",
        },
      });

      setPalletConfig({
        ti: initialVariant.ti ?? "",
        hi: initialVariant.hi ?? "",
      });

      const loose = Number(initialVariant.defaultLooseUnits) || 0;

      setDefaultLooseUnits(loose);
      setLoosePackAllowed(loose > 0 ? "yes" : "no");
      setDefaultCasePack(Number(initialVariant.defaultCasePack) || 1);
      setDefaultInnerMaster(Number(initialVariant.defaultInnerMaster) || 0);
    } else {
      setCurrentVariantName("");
      setPackagingType("Bulk");
      setSelectedBulkSize("1kg");
      setItems([emptyItem()]);
      setDimensions({
        length: { mm: "", in: "" },
        width: { mm: "", in: "" },
        height: { mm: "", in: "" },
      });
      setPalletConfig({ ti: "", hi: "" });
      setLoosePackAllowed("no");
      setDefaultLooseUnits(0);
      setDefaultCasePack(1);
      setDefaultInnerMaster(0);
    }

    lastEditedRef.current = {};
  }, [isOpen, isEditing, initialVariant?._id]);

  const totalCost = items.reduce((s, it, index) => {
    const qty = index < items.length - 2 ? defaultCasePack : Number(it.qty || 0);
    return s + qty * Number(it.unitCost || 0);
  }, 0);

  const handleSave = async () => {
    if (
      !ensureSettingsWriteAccess(
        isEditing ? "update packaging variant" : "create packaging variant"
      )
    ) {
      return;
    }

    if (!currentVariantName.trim()) {
      return toast.error("Variant name is required");
    }

    const validItems = items
      .map((item, index) => ({
        ...item,
        qty: index < items.length - 2 ? defaultCasePack : item.qty,
      }))
      .filter((i) => (i.sku || i.name) && i.qty && Number(i.qty) > 0);

    if (!validItems.length) {
      return toast.error("Add at least one valid item with quantity > 0");
    }

    setSavingVariant(true);

    try {
      const payload = {
        name: currentVariantName.trim(),
        format: packagingType,
        size: packagingType === "Bulk" ? selectedBulkSize : null,
        items: validItems,

        lengthMm: Number(dimensions.length.mm) || 0,
        lengthIn: Number(dimensions.length.in) || 0,
        widthMm: Number(dimensions.width.mm) || 0,
        widthIn: Number(dimensions.width.in) || 0,
        heightMm: Number(dimensions.height.mm) || 0,
        heightIn: Number(dimensions.height.in) || 0,

        ti: Number(palletConfig.ti) || 0,
        hi: Number(palletConfig.hi) || 0,

        defaultLooseUnits:
          loosePackAllowed === "yes"
            ? Math.max(1, Number(defaultLooseUnits) || 1)
            : 0,

        defaultCasePack: Math.max(0, Number(defaultCasePack) || 0),
        defaultInnerMaster: Math.max(0, Number(defaultInnerMaster) || 0),
      };

      const result =
        isEditing && initialVariant
          ? await updatePackagingVariant(initialVariant._id, payload)
          : await createPackagingVariant(payload);

      if (result.success || result.data) {
        toast.success(isEditing ? "Variant updated!" : "Variant created!");
        onSuccess?.();
        onClose();
      } else {
        toast.error("Failed to save variant");
      }
    } catch (err) {
      toast.error("Error saving variant");
      console.error(err);
    } finally {
      setSavingVariant(false);
    }
  };

  const addItem = () => {
    setItems((p) => [...p, emptyItem()]);
  };

  const removeItem = (i) => {
    if (items.length > 1) {
      setItems((p) => p.filter((_, idx) => idx !== i));
    }
  };

  const packagingTypeOptions = [
    { value: "Bulk", label: "Bulk" },
    { value: "Glass", label: "Glass" },
    { value: "Pouch", label: "Pouch" },
    { value: "Plastic", label: "Plastic" },
  ];

  const bulkSizeOptions = [
    { value: "1kg", label: "1 kg" },
    { value: "2.5kg", label: "2.5 kg" },
    { value: "5kg", label: "5 kg" },
    { value: "10kg", label: "10 kg" },
    { value: "25kg", label: "Master case (25 kg)" },
  ];

  const dimRows = [
    { key: "length", label: "Length" },
    { key: "width", label: "Width" },
    { key: "height", label: "Height" },
  ];

  if (!isOpen) return null;


  return (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
    
    {/* MODAL */}
    <div
      className="
        relative
        w-[70%]
        max-w-7xl
        h-[92vh]
        bg-[#f8fafc]
        rounded-3xl
        shadow-2xl
        justify-center
        overflow-hidden
        flex
        flex-col
      "
    >

      {/* HEADER */}
      <div
        className="
          flex
          items-center
          justify-between
          px-8
          py-3
          bg-gradient-to-r
          from-gray-950
          via-gray-900
          to-gray-800
          border-b
          border-gray-800
          shrink-0
        "
      >
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight">
            {isEditing
              ? "Edit Packaging Variant"
              : "Create Packaging Variant"}
          </h2>

          <p className="text-sm text-gray-300 mt-1">
            Paste rows directly from Excel / Google Sheets
          </p>
        </div>

        <button
          onClick={onClose}
          className="
            h-11
            w-11
            rounded-full
            bg-white/10
            hover:bg-white/20
            transition-all
            flex
            items-center
            justify-center
            text-white
          "
        >
          <X size={16} />
        </button>
      </div>

      {/* BODY */}
      <div
        className="
          flex-1
          overflow-y-auto
          px-8
          py-6
          space-y-6
        "
      >

        {/* CONFIG */}
        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6">
          
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-2xl bg-black text-white flex items-center justify-center">
              <Package size={18} />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Configuration
              </h3>

              <p className="text-sm text-gray-500">
                Basic packaging setup
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">

            {/* VARIANT */}
            <div className="xl:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variant Name
              </label>

              <input
                type="text"
                value={currentVariantName}
                placeholder="e.g. Standard Bulk Pack"
                onChange={(e) =>
                  setCurrentVariantName(e.target.value)
                }
                className="
                  w-full
                  h-12
                  rounded-2xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  text-sm
                  outline-none
                  focus:ring-2
                  focus:ring-black/10
                  focus:border-black
                "
              />
            </div>

            {/* PACKAGING TYPE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Packaging Type
              </label>

              <Select
                options={packagingTypeOptions}
                value={
                  packagingTypeOptions.find(
                    (o) => o.value === packagingType
                  ) || null
                }
                onChange={(opt) =>
                  setPackagingType(opt?.value || "Bulk")
                }
                styles={lightSelectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            {/* CASE PACK */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Case Pack
              </label>

              <input
                type="number"
                min="0"
                value={defaultCasePack}
                onChange={(e) =>
                  setDefaultCasePack(
                    Math.max(0, Number(e.target.value) || 0)
                  )
                }
                className="
                  w-full
                  h-12
                  rounded-2xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  text-sm
                  outline-none
                  focus:ring-2
                  focus:ring-black/10
                  focus:border-black
                "
              />
            </div>

            {/* INNER MASTER */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Inner Master
              </label>

              <input
                type="number"
                min="0"
                value={defaultInnerMaster}
                onChange={(e) =>
                  setDefaultInnerMaster(
                    Math.max(0, Number(e.target.value) || 0)
                  )
                }
                className="
                  w-full
                  h-12
                  rounded-2xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  text-sm
                  outline-none
                  focus:ring-2
                  focus:ring-black/10
                  focus:border-black
                "
              />
            </div>

            {/* TI (Tier) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                TI (Tier)
              </label>
              <input
                type="number"
                min="0"
                value={palletConfig.ti}
                onChange={(e) =>
                  setPalletConfig((prev) => ({ ...prev, ti: e.target.value }))
                }
                placeholder="1"
                className="
                  w-full
                  h-12
                  rounded-2xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  text-sm
                  outline-none
                  focus:ring-2
                  focus:ring-black/10
                  focus:border-black
                "
              />
            </div>

            {/* HI (High) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HI (High)
              </label>
              <input
                type="number"
                min="0"
                value={palletConfig.hi}
                onChange={(e) =>
                  setPalletConfig((prev) => ({ ...prev, hi: e.target.value }))
                }
                placeholder="1"
                className="
                  w-full
                  h-12
                  rounded-2xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  text-sm
                  outline-none
                  focus:ring-2
                  focus:ring-black/10
                  focus:border-black
                "
              />
            </div>
          </div>
        </div>

        {/* DIMENSIONS */}
        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6">

          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-2xl bg-black text-white flex items-center justify-center">
              <Ruler size={18} />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Dimensions
              </h3>

              <p className="text-sm text-gray-500">
                MM / Inches auto convert
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200">
            
            <table className="w-full border-collapse">

              <thead className="bg-gray-100">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 border-r">
                    Dimension
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 border-r">
                    MM
                  </th>

                  <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600">
                    Inches
                  </th>
                </tr>
              </thead>

              <tbody>
                {dimRows.map(({ key, label }) => (
                  <tr
                    key={key}
                    className="border-t border-gray-200 hover:bg-gray-50"
                  >

                    <td className="px-5 py-4 bg-gray-50 border-r text-sm font-medium text-gray-700">
                      {label}
                    </td>

                    <td className="border-r">
                      <input
                        type="number"
                        min={0}
                        value={dimensions[key].mm}
                        onChange={(e) =>
                          handleDimChange(
                            key,
                            "mm",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          handleDimBlur(key, "mm")
                        }
                        className="
                          w-full
                          h-12
                          px-4
                          bg-transparent
                          outline-none
                          text-sm
                          focus:bg-blue-50
                        "
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min={0}
                        step="0.0625"
                        value={dimensions[key].in}
                        onChange={(e) =>
                          handleDimChange(
                            key,
                            "in",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          handleDimBlur(key, "in")
                        }
                        className="
                          w-full
                          h-12
                          px-4
                          bg-transparent
                          outline-none
                          text-sm
                          focus:bg-blue-50
                        "
                      />
                    </td>
                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        </div>

        {/* PACKAGING ITEMS */}
        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6">

          <div className="flex items-center justify-between mb-6">

            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-black text-white flex items-center justify-center">
                <Layers size={18} />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Packaging Items
                </h3>

                <p className="text-sm text-gray-500">
                  Last 2 rows editable
                </p>
              </div>
            </div>

            <button
              onClick={addItem}
              className="
                h-11
                px-5
                rounded-2xl
                bg-black
                hover:bg-gray-900
                text-white
                text-sm
                font-semibold
                flex
                items-center
                gap-2
              "
            >
              <Plus size={16} />
              Add Row
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200">

            <div className="overflow-x-auto">

              <table className="w-full min-w-[950px] border-collapse">

                <thead className="bg-gray-100">
                  <tr>
                    {[
                      "#",
                      "Item Type",
                      "SKU",
                      "Name",
                      "Qty",
                      "Unit Cost",
                      "",
                    ].map((h, i) => (
                      <th
                        key={i}
                        className="
                          px-4
                          py-4
                          text-left
                          text-xs
                          font-semibold
                          text-gray-600
                          border-r
                          last:border-r-0
                        "
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => {

                    const isDefaultQtyRow =
                      index < items.length - 2;

                    return (
                      <tr
                        key={item.id}
                        className="
                          border-t
                          border-gray-200
                          hover:bg-gray-50
                        "
                      >

                        {/* INDEX */}
                        <td className="px-4 py-3 text-xs text-gray-500 border-r bg-gray-50">
                          {index + 1}
                        </td>

                        {/* ITEM TYPE */}
                        <td className="border-r p-0">
                          <Select
                            options={packagingItemTypeOptions}
                            value={
                              packagingItemTypeOptions.find(
                                (o) =>
                                  o.value === item.itemType
                              ) || null
                            }
                            onChange={(opt) =>
                              setItems((p) => {
                                const u = [...p];

                                u[index] = {
                                  ...u[index],
                                  itemType:
                                    opt?.value || "",
                                };

                                return u;
                              })
                            }
                            placeholder="Select"
                            isClearable
                            menuPortalTarget={document.body}
                            styles={cellSelectStyles}
                          />
                        </td>

                        {/* SKU */}
                        <td className="border-r p-0">
                          <input
                            type="text"
                            value={item.sku}
                            onChange={(e) =>
                              handleSkuChange(
                                index,
                                e.target.value
                              )
                            }
                            onPaste={(e) =>
                              handleItemsPaste(
                                e,
                                index
                              )
                            }
                            className="
                              w-full
                              h-12
                              px-4
                              bg-transparent
                              outline-none
                              text-sm
                              font-mono
                              uppercase
                              focus:bg-blue-50
                            "
                          />
                        </td>

                        {/* NAME */}
                        <td className="border-r p-0">
                          <input
                            type="text"
                            value={item.name}
                            readOnly
                            className="
                              w-full
                              h-12
                              px-4
                              bg-gray-50
                              text-sm
                              text-gray-700
                              outline-none
                            "
                          />
                        </td>

                        {/* QTY */}
                        <td className="border-r p-0">
                          <input
                            type="number"
                            min="1"
                            value={
                              isDefaultQtyRow
                                ? defaultCasePack
                                : item.qty
                            }
                            onChange={(e) => {

                              if (isDefaultQtyRow)
                                return;

                              setItems((p) => {
                                const u = [...p];

                                u[index] = {
                                  ...u[index],
                                  qty: e.target.value,
                                };

                                return u;
                              });
                            }}
                            readOnly={
                              isDefaultQtyRow
                            }
                            className={`
                              w-full
                              h-12
                              px-4
                              text-sm
                              outline-none
                              ${
                                isDefaultQtyRow
                                  ? "bg-gray-100 text-gray-700 font-semibold cursor-not-allowed"
                                  : "bg-transparent focus:bg-blue-50"
                              }
                            `}
                          />
                        </td>

                        {/* UNIT COST */}
                        <td className="border-r p-0">
                          <input
                            type="text"
                            value={Number(
                              item.unitCost || 0
                            ).toFixed(4)}
                            readOnly
                            className="
                              w-full
                              h-12
                              px-4
                              bg-gray-50
                              text-sm
                              font-mono
                              text-gray-700
                              outline-none
                            "
                          />
                        </td>

                        {/* DELETE */}
                        <td className="text-center">
                          <button
                            onClick={() =>
                              removeItem(index)
                            }
                            disabled={
                              items.length === 1
                            }
                            className="
                              p-2
                              rounded-lg
                              text-gray-400
                              hover:text-red-500
                              hover:bg-red-50
                              transition
                            "
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

              </table>
            </div>
          </div>

          {/* TOTAL */}
          <div className="mt-6 flex justify-end">
            <div className="bg-gray-50 rounded-2xl px-6 py-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total Packaging Cost
              </p>

              <p className="text-3xl font-bold text-green-600 mt-1">
                ${totalCost.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* FOOTER */}
      <div
        className="
          shrink-0
          border-t
          border-gray-200
          bg-white
          px-8
          py-5
          flex
          justify-end
          gap-4
        "
      >

        <button
          onClick={onClose}
          className="
            h-12
            px-6
            rounded-2xl
            border
            border-gray-300
            text-sm
            font-medium
            hover:bg-gray-100
          "
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={
            savingVariant ||
            !canManageCostingSettings
          }
          className={`
            h-12
            px-7
            rounded-2xl
            text-sm
            font-semibold
            transition-all
            ${
              savingVariant ||
              !canManageCostingSettings
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-black hover:bg-gray-900 text-white"
            }
          `}
        >
          {savingVariant
            ? "Saving..."
            : isEditing
            ? "Update Variant"
            : "Create Variant"}
        </button>
      </div>
    </div>
  </div>
);


};

export default PackagingVariantModal;
